import { useRef } from 'react';
import uniqueId from 'lodash/uniqueId';
import classnames from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import MainToggle, {
  Props as MainToggleProps,
} from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import InputLabelSubText from '@wix/da-ds/pkg/formControls/lego/InputLabelSubText';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import s from './LabelledToggleField.scss';

interface Props
  extends Omit<MainToggleProps, 'value' | 'id' | 'onChange' | 'variant'> {
  name: string;
  label: string;
  subLabel?: string;
  tooltip?: string;
  toggleTooltip?: string;
  onChange?: (newValue: boolean) => void;
}

function LabelledToggleField({
  name,
  label,
  subLabel = '',
  tooltip,
  toggleTooltip,
  className,
  onChange,
  ...props
}: Props) {
  const { current: id } = useRef(uniqueId('select'));
  const { control } = useFormContext();
  // note (famous last words): relatively low need for errors to be parsed...?

  const {
    field: { onChange: controllerOnChange, ref, ...inputProps },
  } = useController({ name, control });

  const toggle = (
    <MainToggle
      variant="main"
      aria-labelledby={id}
      {...props}
      {...inputProps}
      onChange={v => {
        controllerOnChange(v);
        onChange && onChange(v);
      }}
    />
  );

  return (
    <label className={classnames(className, s['root'])} id={id}>
      <InputLabelText className={s['labels']}>
        <span className={s['label-and-tooltip']}>
          {label}
          {tooltip && (
            <SimpleTooltip text={tooltip} className={s['tooltip-icon']}>
              <InfoIcon aria-label={tooltip} size={IconSize.SMALLER} />
            </SimpleTooltip>
          )}
        </span>
        {subLabel && (
          <InputLabelSubText className={classnames(s['sublabel'])}>
            {subLabel}
          </InputLabelSubText>
        )}
      </InputLabelText>
      {toggleTooltip ? (
        <SimpleTooltip text={toggleTooltip} usePortal>
          {toggle}
        </SimpleTooltip>
      ) : (
        toggle
      )}
    </label>
  );
}

export default LabelledToggleField;
